"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Thesis — full-bleed hero card that locks the demo's thesis in the first
 * 30 seconds. Renders during the idle state (before any scenario has
 * started) in place of SceneRouter's default routing. Fades out as soon
 * as the first SSE event arrives.
 *
 * The four-trade arc is the demo's single strongest narrative decision;
 * the framing card states it up-front so the audience reads what follows
 * through that lens. T4 adds the air-cargo modality on top of the three
 * sea-freight trades.
 */
export function Thesis({ className }: { className?: string }) {
  const t = useTranslations("thesis");
  return (
    <div className={clsx("relative grid h-full place-items-center overflow-hidden", className)}>
      {/* Subtle pillar-glow backdrop — three faint orbs in pillar colors
       *  positioned across the canvas to hint at Verify / Trace / Finance
       *  before they're named. */}
      <div aria-hidden="true" className="absolute inset-0 -z-10">
        <div className="absolute left-1/4 top-1/3 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-state-verified/15 blur-3xl" />
        <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-state-traced/15 blur-3xl" />
        <div className="absolute left-3/4 top-2/3 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-state-financed/15 blur-3xl" />
      </div>

      <div className="glass max-w-4xl px-12 py-14 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-state-verified">
          {t("kicker")}
        </p>
        <h1 className="mt-4 font-display text-5xl font-semibold leading-tight text-orbitra-paper">
          {t("headlineLine1")}<br />
          {t("headlineLine2")}<br />
          <span className="text-state-traced">{t("headlineLine3")}</span>
        </h1>
        <div className="mt-8 grid grid-cols-4 gap-3 text-start">
          <ThesisPillar tone="verified" kicker="T1" title={t("pillarT1Title")} sub={t("pillarT1Sub")} />
          <ThesisPillar tone="traced"   kicker="T2" title={t("pillarT2Title")} sub={t("pillarT2Sub")} />
          <ThesisPillar tone="financed" kicker="T3" title={t("pillarT3Title")} sub={t("pillarT3Sub")} />
          <ThesisPillar tone="air"      kicker="T4" title={t("pillarT4Title")} sub={t("pillarT4Sub")} />
        </div>
        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
          {t("beginHint").split("N").map((part, i, arr) =>
            i < arr.length - 1 ? (
              <span key={i}>
                {part}
                <kbd className="mx-1 rounded border border-state-verified/40 bg-state-verified/10 px-2 py-0.5 text-state-verified">N</kbd>
              </span>
            ) : (
              <span key={i}>{part}</span>
            ),
          )}
        </p>
      </div>
    </div>
  );
}

function ThesisPillar({
  tone,
  kicker,
  title,
  sub,
}: {
  tone: "verified" | "traced" | "financed" | "air";
  kicker: string;
  title: string;
  sub: string;
}) {
  // T4's "air" tone reuses the financed gold accent (T4's defining beat
  // is the fast-track LC) but with a brighter border to differentiate
  // it from T3 visually. Future polish could introduce a dedicated
  // state-air CSS variable; for now the contrast is intentional.
  const palette = {
    verified: "border-state-verified/30 bg-state-verified/8 text-state-verified",
    traced:   "border-state-traced/30 bg-state-traced/8 text-state-traced",
    financed: "border-state-financed/30 bg-state-financed/8 text-state-financed",
    air:      "border-state-financed/60 bg-state-financed/12 text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]",
  }[tone];
  return (
    <div className={clsx("rounded-md border px-4 py-3", palette)}>
      <p className="font-mono text-[9px] uppercase tracking-[0.18em]">{kicker}</p>
      <p className="mt-1 font-display text-base font-semibold text-orbitra-paper">{title}</p>
      <p className="mt-0.5 text-[11px] text-orbitra-teal-soft">{sub}</p>
    </div>
  );
}
